import React from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../notifications';
import { Spinner, ToggleField } from '../../../shared/components';
import {
  createVoiceChannelAccount,
  getVoiceChannelAccountSettings,
  testVoiceChannelAccountConnection,
  updateVoiceChannelAccountCredentials,
} from '../services/channelAccountService';
import { ChannelAccountVoiceSettings, CallingConnectionTestResult } from '../types/channelAccount';
import { getInboundPopSettings, InboundPopSettings } from '../../calling/services/callingService';
import { useChannelAccounts } from '../hooks/useChannelAccounts';
import { ChannelAccountBar } from './ChannelAccountBar';
import { ConnectorFieldStatus } from './ConnectorFieldStatus';

const emptyVoiceSettingsForm = {
  displayName: '3CX',
  serverUrl: '',
  clientId: '',
  clientSecret: '',
  defaultCallerId: '',
  inboundEnabled: true,
  outboundEnabled: true,
  aiEnabled: false,
  aiAnswerMode: 'Off',
  aiAnsweredNumbers: '',
  handoffTarget: '',
  greeting: '',
  businessHoursStart: '09:00',
  businessHoursEnd: '18:00',
  businessHoursTimeZone: '',
  businessHoursWeekdays: '1,2,3,4,5',
  twilioAccountSid: '',
  twilioAuthToken: '',
  twilioFromNumber: '',
};

interface CallsConnectorTabProps {
  canManage: boolean;
}

export const CallsConnectorTab: React.FC<CallsConnectorTabProps> = ({ canManage }) => {
  const { t } = useTranslation(['companies', 'admin', 'calling']);
  const { showToast } = useToast();
  const voiceChannelAccounts = useChannelAccounts('Voice', true);

  const [loadingSettings, setLoadingSettings] = React.useState(false);
  const [savingSettings, setSavingSettings] = React.useState(false);
  const [testingConnection, setTestingConnection] = React.useState(false);
  const [accountSettings, setAccountSettings] = React.useState<ChannelAccountVoiceSettings | null>(null);
  const [settingsForm, setSettingsForm] = React.useState(emptyVoiceSettingsForm);
  const [settingsError, setSettingsError] = React.useState<string | null>(null);
  const [settingsNotice, setSettingsNotice] = React.useState<string | null>(null);
  const [latestTestResult, setLatestTestResult] = React.useState<CallingConnectionTestResult | null>(null);
  const [showAddAccount, setShowAddAccount] = React.useState(false);
  const [popSettings, setPopSettings] = React.useState<InboundPopSettings | null>(null);
  const [copiedField, setCopiedField] = React.useState<string | null>(null);

  const loadAccountSettings = React.useCallback(async (accountId: string) => {
    setLoadingSettings(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      const data = await getVoiceChannelAccountSettings(accountId);
      setAccountSettings(data);
      setLatestTestResult(null);
      setSettingsForm({
        displayName: '3CX',
        serverUrl: data.serverUrl || '',
        clientId: '',
        clientSecret: '',
        defaultCallerId: data.defaultCallerId || '',
        inboundEnabled: data.inboundEnabled,
        outboundEnabled: data.outboundEnabled,
        aiEnabled: Boolean(data.voiceAgent?.enabled),
        aiAnswerMode: data.voiceAgent?.answerMode || 'Off',
        aiAnsweredNumbers: data.voiceAgent?.answeredNumbers || '',
        handoffTarget: data.voiceAgent?.handoffTarget || '',
        greeting: data.voiceAgent?.greeting || '',
        businessHoursStart: data.voiceAgent?.businessHoursStart || '09:00',
        businessHoursEnd: data.voiceAgent?.businessHoursEnd || '18:00',
        businessHoursTimeZone: data.voiceAgent?.businessHoursTimeZone || '',
        businessHoursWeekdays: data.voiceAgent?.businessHoursWeekdays || '1,2,3,4,5',
        twilioAccountSid: '',
        twilioAuthToken: '',
        twilioFromNumber: data.voiceAgent?.twilioFromNumber || '',
      });
    } catch (error) {
      setSettingsError(error instanceof Error ? error.message : t('failedToLoadVoiceSettings'));
    } finally {
      setLoadingSettings(false);
    }
  }, [t]);

  React.useEffect(() => {
    const accountId = voiceChannelAccounts.selectedAccountId;
    if (!accountId) {
      setAccountSettings(null);
      setSettingsForm(emptyVoiceSettingsForm);
      return;
    }

    void loadAccountSettings(accountId);
  }, [loadAccountSettings, voiceChannelAccounts.selectedAccountId]);

  React.useEffect(() => {
    const accountId = voiceChannelAccounts.selectedAccountId;
    if (!accountId || !canManage) {
      setPopSettings(null);
      return;
    }

    let cancelled = false;
    // The secret is created on first read, so only ask for it when someone can actually configure 3CX.
    void getInboundPopSettings(accountId)
      .then(settings => {
        if (!cancelled) {
          setPopSettings(settings);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPopSettings(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canManage, voiceChannelAccounts.selectedAccountId]);

  const copyValue = async (field: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      window.setTimeout(() => setCopiedField(current => (current === field ? null : current)), 2000);
    } catch {
      showToast(value, 'info');
    }
  };

  const saveSettings = async () => {
    const selectedAccount = voiceChannelAccounts.selectedAccount;
    const isInitialSetup = voiceChannelAccounts.accounts.length === 0;

    if (!selectedAccount && !isInitialSetup) {
      return;
    }

    if (isInitialSetup && (!settingsForm.serverUrl.trim() || !settingsForm.clientId.trim() || !settingsForm.clientSecret.trim())) {
      const message = t('voiceCredentialsRequired');
      setSettingsError(message);
      showToast(message, 'error');
      return;
    }

    setSavingSettings(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      if (isInitialSetup) {
        await createVoiceChannelAccount({
          displayName: settingsForm.displayName.trim() || '3CX',
          serverUrl: settingsForm.serverUrl.trim(),
          clientId: settingsForm.clientId.trim(),
          clientSecret: settingsForm.clientSecret.trim(),
          inboundEnabled: settingsForm.inboundEnabled,
          outboundEnabled: settingsForm.outboundEnabled,
          defaultCallerId: settingsForm.defaultCallerId.trim() || undefined,
          isDefault: true,
        });
        await voiceChannelAccounts.reload();
      } else if (selectedAccount) {
        await updateVoiceChannelAccountCredentials(selectedAccount.id, {
          serverUrl: settingsForm.serverUrl.trim() || undefined,
          clientId: settingsForm.clientId.trim() || undefined,
          clientSecret: settingsForm.clientSecret.trim() || undefined,
          inboundEnabled: settingsForm.inboundEnabled,
          outboundEnabled: settingsForm.outboundEnabled,
          defaultCallerId: settingsForm.defaultCallerId.trim() || undefined,
          voiceAgent: {
            enabled: settingsForm.aiEnabled,
            answerMode: settingsForm.aiEnabled ? settingsForm.aiAnswerMode : 'Off',
            answeredNumbers: settingsForm.aiAnsweredNumbers.trim(),
            handoffTarget: settingsForm.handoffTarget.trim(),
            greeting: settingsForm.greeting.trim(),
            businessHoursStart: settingsForm.businessHoursStart.trim(),
            businessHoursEnd: settingsForm.businessHoursEnd.trim(),
            businessHoursTimeZone: settingsForm.businessHoursTimeZone.trim(),
            businessHoursWeekdays: settingsForm.businessHoursWeekdays.trim(),
            twilioAccountSid: settingsForm.twilioAccountSid.trim() || undefined,
            twilioAuthToken: settingsForm.twilioAuthToken.trim() || undefined,
            twilioFromNumber: settingsForm.twilioFromNumber.trim(),
          },
        });
        await loadAccountSettings(selectedAccount.id);
        await voiceChannelAccounts.reload();
      }

      setSettingsForm(prev => ({
        ...prev,
        clientId: '',
        clientSecret: '',
        twilioAccountSid: '',
        twilioAuthToken: '',
      }));
      setSettingsNotice(t('voiceSettingsSaved'));
      showToast(t('voiceSettingsSaved'), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToSaveVoiceSettings');
      setSettingsError(message);
      showToast(message, 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const testConnection = async () => {
    const selectedAccount = voiceChannelAccounts.selectedAccount;
    if (!selectedAccount) {
      return;
    }

    setTestingConnection(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      const result = await testVoiceChannelAccountConnection(selectedAccount.id);
      setLatestTestResult(result);
      if (result.succeeded) {
        setSettingsNotice(result.message || t('voiceConnectionSucceeded'));
        showToast(result.message || t('voiceConnectionSucceeded'), 'success');
      } else {
        const message = result.message || t('failedToTestVoiceConnection');
        setSettingsError(message);
        showToast(message, 'error');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToTestVoiceConnection');
      setSettingsError(message);
      showToast(message, 'error');
    } finally {
      setTestingConnection(false);
    }
  };

  return (
    <section className="company-card">
      <ChannelAccountBar
        channel="Voice"
        accounts={voiceChannelAccounts.accounts}
        limits={voiceChannelAccounts.limits}
        loading={voiceChannelAccounts.loading}
        error={voiceChannelAccounts.error}
        selectedAccountId={voiceChannelAccounts.selectedAccountId}
        onSelectAccount={voiceChannelAccounts.setSelectedAccountId}
        onRefresh={() => { void voiceChannelAccounts.reload(); }}
        canManage={canManage}
        showAddForm={showAddAccount}
        onToggleAddForm={() => setShowAddAccount(current => !current)}
        onAccountUpdated={() => { void voiceChannelAccounts.reload(); }}
      />

      {showAddAccount && canManage ? (
        <div className="company-form-grid" style={{ marginBottom: 16 }}>
          <p style={{ color: '#516173', marginTop: 0 }}>
            {t('channelAccountAddVoiceHelp')}
          </p>
        </div>
      ) : null}

      {voiceChannelAccounts.loading && voiceChannelAccounts.accounts.length === 0 ? (
        <Spinner />
      ) : (
        <>
          <h3>{t('voiceIntegrationHeading')}</h3>
          <p style={{ color: '#516173', marginTop: 0 }}>
            {t('voiceIntegrationHelp')}
          </p>
          <p style={{ color: '#516173', marginTop: 0, fontSize: '0.85rem' }}>
            {t('addonNotice', { ns: 'calling' })}{' '}
            <a href="/plans">{t('addonNoticeLink', { ns: 'calling' })}</a>
          </p>

          {settingsError ? <div className="error">{settingsError}</div> : null}
          {settingsNotice ? <div className="success">{settingsNotice}</div> : null}

          {loadingSettings ? <Spinner /> : (
            <div className="company-form-grid">
              <label>
                {t('voiceServerUrl')}
                <input
                  className="text-input"
                  value={settingsForm.serverUrl}
                  onChange={event => setSettingsForm(prev => ({ ...prev, serverUrl: event.target.value }))}
                  placeholder="https://pbx.company.com"
                  disabled={!canManage}
                />
              </label>
              <label>
                {t('voiceClientId')}
                <input
                  className="text-input"
                  value={settingsForm.clientId}
                  onChange={event => setSettingsForm(prev => ({ ...prev, clientId: event.target.value }))}
                  placeholder={accountSettings?.hasClientId ? t('voiceCredentialConfigured') : ''}
                  disabled={!canManage}
                  autoComplete="off"
                />
              </label>
              <label>
                {t('voiceClientSecret')}
                <input
                  className="text-input"
                  type="password"
                  value={settingsForm.clientSecret}
                  onChange={event => setSettingsForm(prev => ({ ...prev, clientSecret: event.target.value }))}
                  placeholder={accountSettings?.hasClientSecret ? t('voiceCredentialConfigured') : ''}
                  disabled={!canManage}
                  autoComplete="new-password"
                />
              </label>
              <label>
                {t('voiceDefaultCallerId')}
                <input
                  className="text-input"
                  value={settingsForm.defaultCallerId}
                  onChange={event => setSettingsForm(prev => ({ ...prev, defaultCallerId: event.target.value }))}
                  placeholder="+99412..."
                  disabled={!canManage}
                />
              </label>
              <ToggleField
                label={t('voiceInboundEnabled')}
                checked={settingsForm.inboundEnabled}
                onChange={value => setSettingsForm(prev => ({ ...prev, inboundEnabled: value }))}
                disabled={!canManage}
              />
              <ToggleField
                label={t('voiceOutboundEnabled')}
                checked={settingsForm.outboundEnabled}
                onChange={value => setSettingsForm(prev => ({ ...prev, outboundEnabled: value }))}
                disabled={!canManage}
              />
            </div>
          )}

          {!loadingSettings ? (
            <div style={{ marginTop: 24, borderTop: '1px solid rgba(15, 23, 36, 0.08)', paddingTop: 16 }}>
              <h3 style={{ marginTop: 0 }}>{t('voiceAgentHeading')}</h3>
              <p style={{ color: '#516173', marginTop: 0 }}>{t('voiceAgentHelp')}</p>
              <div className="company-form-grid">
                <ToggleField
                  label={t('voiceAgentEnabled')}
                  checked={settingsForm.aiEnabled}
                  onChange={value => setSettingsForm(prev => ({
                    ...prev,
                    aiEnabled: value,
                    aiAnswerMode: value && prev.aiAnswerMode === 'Off' ? 'Always' : prev.aiAnswerMode,
                  }))}
                  disabled={!canManage}
                />
                <label>
                  {t('voiceAgentMode')}
                  <select
                    className="text-input"
                    value={settingsForm.aiAnswerMode}
                    onChange={event => setSettingsForm(prev => ({ ...prev, aiAnswerMode: event.target.value }))}
                    disabled={!canManage || !settingsForm.aiEnabled}
                  >
                    <option value="Off">{t('voiceAgentModeOff')}</option>
                    <option value="Always">{t('voiceAgentModeAlways')}</option>
                    <option value="AfterHours">{t('voiceAgentModeAfterHours')}</option>
                  </select>
                </label>
                <label>
                  {t('voiceAgentAnsweredNumbers')}
                  <input
                    className="text-input"
                    value={settingsForm.aiAnsweredNumbers}
                    onChange={event => setSettingsForm(prev => ({ ...prev, aiAnsweredNumbers: event.target.value }))}
                    placeholder="+15551230000, +15551239999"
                    disabled={!canManage}
                  />
                  <span className="field-hint">{t('voiceAgentAnsweredNumbersHint')}</span>
                </label>
                <label>
                  {t('voiceAgentHandoffTarget')}
                  <input
                    className="text-input"
                    value={settingsForm.handoffTarget}
                    onChange={event => setSettingsForm(prev => ({ ...prev, handoffTarget: event.target.value }))}
                    placeholder="100 or +1555... or sip:100@pbx"
                    disabled={!canManage}
                  />
                  <span className="field-hint">{t('voiceAgentHandoffTargetHint')}</span>
                </label>
                <label>
                  {t('voiceAgentGreeting')}
                  <input
                    className="text-input"
                    value={settingsForm.greeting}
                    onChange={event => setSettingsForm(prev => ({ ...prev, greeting: event.target.value }))}
                    disabled={!canManage}
                  />
                </label>
                <label>
                  {t('voiceAgentBusinessHoursStart')}
                  <input
                    className="text-input"
                    type="time"
                    value={settingsForm.businessHoursStart}
                    onChange={event => setSettingsForm(prev => ({ ...prev, businessHoursStart: event.target.value }))}
                    disabled={!canManage}
                  />
                </label>
                <label>
                  {t('voiceAgentBusinessHoursEnd')}
                  <input
                    className="text-input"
                    type="time"
                    value={settingsForm.businessHoursEnd}
                    onChange={event => setSettingsForm(prev => ({ ...prev, businessHoursEnd: event.target.value }))}
                    disabled={!canManage}
                  />
                </label>
                <label>
                  {t('voiceAgentBusinessHoursTimeZone')}
                  <input
                    className="text-input"
                    value={settingsForm.businessHoursTimeZone}
                    onChange={event => setSettingsForm(prev => ({ ...prev, businessHoursTimeZone: event.target.value }))}
                    placeholder="UTC"
                    disabled={!canManage}
                  />
                </label>
                <label>
                  {t('voiceAgentBusinessHoursWeekdays')}
                  <input
                    className="text-input"
                    value={settingsForm.businessHoursWeekdays}
                    onChange={event => setSettingsForm(prev => ({ ...prev, businessHoursWeekdays: event.target.value }))}
                    placeholder="1,2,3,4,5"
                    disabled={!canManage}
                  />
                </label>
                <label>
                  {t('voiceAgentTwilioAccountSid')}
                  <input
                    className="text-input"
                    value={settingsForm.twilioAccountSid}
                    onChange={event => setSettingsForm(prev => ({ ...prev, twilioAccountSid: event.target.value }))}
                    placeholder={accountSettings?.voiceAgent?.hasTwilioAccountSid ? t('voiceCredentialConfigured') : 'ACxxxxxxxx'}
                    disabled={!canManage}
                    autoComplete="off"
                  />
                </label>
                <label>
                  {t('voiceAgentTwilioAuthToken')}
                  <input
                    className="text-input"
                    type="password"
                    value={settingsForm.twilioAuthToken}
                    onChange={event => setSettingsForm(prev => ({ ...prev, twilioAuthToken: event.target.value }))}
                    placeholder={accountSettings?.voiceAgent?.hasTwilioAuthToken ? t('voiceCredentialConfigured') : ''}
                    disabled={!canManage}
                    autoComplete="new-password"
                  />
                </label>
                <label>
                  {t('voiceAgentTwilioFromNumber')}
                  <input
                    className="text-input"
                    value={settingsForm.twilioFromNumber}
                    onChange={event => setSettingsForm(prev => ({ ...prev, twilioFromNumber: event.target.value }))}
                    placeholder="+1555..."
                    disabled={!canManage}
                  />
                </label>
              </div>
              <p style={{ color: '#516173', fontSize: '0.85rem' }}>{t('voiceAgentTwilioHint')}</p>
              <div style={{ marginTop: 12 }}>
                <p>{t('voiceAgentTwilioAccountSid')}: <ConnectorFieldStatus done={Boolean(accountSettings?.voiceAgent?.hasTwilioAccountSid)} /></p>
                <p>{t('voiceAgentTwilioAuthToken')}: <ConnectorFieldStatus done={Boolean(accountSettings?.voiceAgent?.hasTwilioAuthToken)} /></p>
              </div>
              {accountSettings?.voiceAgent?.incomingWebhookUrl ? (
                <div style={{ marginTop: 12 }}>
                  {[
                    { key: 'voiceIncoming', label: t('voiceAgentIncomingWebhook'), value: accountSettings.voiceAgent.incomingWebhookUrl },
                    { key: 'voiceStatus', label: t('voiceAgentStatusCallback'), value: accountSettings.voiceAgent.statusCallbackUrl || '' },
                  ].filter(item => item.value).map(item => (
                    <label key={item.key} style={{ display: 'block', marginTop: 8 }}>
                      {item.label}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input className="text-input" readOnly value={item.value} />
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => { void copyValue(item.key, item.value); }}
                        >
                          {copiedField === item.key ? t('copied', { ns: 'calling' }) : t('copy', { ns: 'calling' })}
                        </button>
                      </div>
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <div style={{ marginTop: 12 }}>
            <p>{t('voiceClientId')}: <ConnectorFieldStatus done={Boolean(accountSettings?.hasClientId)} /></p>
            <p>{t('voiceClientSecret')}: <ConnectorFieldStatus done={Boolean(accountSettings?.hasClientSecret)} /></p>
          </div>

          {latestTestResult ? (
            <p style={{ color: latestTestResult.succeeded ? '#0f7b3d' : '#b42318', marginTop: 12 }}>
              {latestTestResult.message}
            </p>
          ) : null}

          {canManage ? (
            <div className="company-actions-inline" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => { void saveSettings(); }}
                disabled={savingSettings || loadingSettings}
              >
                {savingSettings ? t('savingEllipsis', { ns: 'admin' }) : t('saveVoiceSettings')}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { void testConnection(); }}
                disabled={testingConnection || !voiceChannelAccounts.selectedAccount}
              >
                {testingConnection ? t('savingEllipsis', { ns: 'admin' }) : t('testVoiceConnection')}
              </button>
            </div>
          ) : null}

          {popSettings ? (
            <div style={{ marginTop: 24, borderTop: '1px solid rgba(15, 23, 36, 0.08)', paddingTop: 16 }}>
              <h3 style={{ marginTop: 0 }}>{t('inboundPopHeading', { ns: 'calling' })}</h3>
              <p style={{ color: '#516173', marginTop: 0 }}>
                {t('inboundPopDescription', { ns: 'calling' })}
              </p>
              {[
                { key: 'popUrl', label: t('inboundPopUrl', { ns: 'calling' }), value: popSettings.popUrlTemplate },
                { key: 'lookupUrl', label: t('inboundPopLookupUrl', { ns: 'calling' }), value: popSettings.lookupUrlTemplate },
                { key: 'secret', label: t('inboundPopSecret', { ns: 'calling' }), value: popSettings.crmLookupSecret },
              ].map(field => (
                <label key={field.key} style={{ display: 'block', marginTop: 10 }}>
                  {field.label}
                  <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input className="text-input" value={field.value} readOnly onFocus={event => event.target.select()} />
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => { void copyValue(field.key, field.value); }}
                    >
                      {copiedField === field.key ? t('copied', { ns: 'calling' }) : t('copy', { ns: 'calling' })}
                    </button>
                  </span>
                </label>
              ))}
              <p style={{ color: '#516173', fontSize: '0.85rem' }}>
                {t('inboundPopSecretHint', { ns: 'calling' })}
              </p>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};
